import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React from 'react';
import DatePickerValue from '../../../DatePicker';
import { NumericFormat } from 'react-number-format';
import PropTypes from "prop-types";

const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      decimalScale={2}
      fixedDecimalScale
      thousandSeparator
      valueIsNumericString
    />
  );
});

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function AdditionalContractInfo({
  msaDate, setMsaDate,
  sowDate, setSowDate,
  autoRenewalPeriod, setAutoRenewal,
  initialTerms, setInitialTerms,
  cancellationNotice, setCancellationNotice,
  requiredInspections, setRequiredInspections,
  liabilityLimitAmount, setLiabilityLimitAmount,
  companyLegalName, setCompanyLegalName,
  stateOfOrganization, setStateOfOrganization,
  legalEntityType, setLegalEntityType,
  assignmentProvision, setAssignmentProvision,
  installationCode, setInstallationCode,
}) {
    const gridStyle = {
        paddingBottom: 40,
    };

    const requiredInspectionsOptions = [
        {
          id: 0,
          name: "Quarterly",
        },
        {
          id: 1,
          name: "Annually",
        },
        {
          id: 2,
          name: "Periodic",
        },
        {
          id: 3,
          name: "No Requirement",
        },
        {
          id: 4,
          name: "Other - Refer to Original Contract",
        },
    ];

    const assignmentProvisionOptions = [
        {
          id: 0,
          name: "Freely Assignable",
        },
        {
          id: 1,
          name: "Assignable with Advance Notice",
        },
        {
          id: 2,
          name: "Assignable with Notice After",
        },
        {
          id: 3,
          name: "Assignable with Consent or Not Assignable",
        },
        {
          id: 4,
          name: "Other - Refer to Original Contract",
        },
    ];

    const installationCodeOptions = [
        {
          id: 0,
          name: "None",
        },
        {
          id: 1,
          name: "JTFI",
        },
        {
          id: 2,
          name: "JTSE",
        },
        {
          id: 3,
          name: "JTTE",
        },
        {
          id: 4,
          name: "SCFI",
        },
        {
          id: 5,
          name: "Other",
        },
    ];

    const legalEntityTypeOptions = [
        {
          id: 0,
          name: "Corporation",
        },
        {
          id: 1,
          name: "Limited Liability Company",
        },
        {
          id: 2,
          name: "Partnership",
        },
        {
          id: 3,
          name: "Other",
        },
    ];

    const initialTermsOptions = [
        {
          id: 0,
          name: "1 Year",
        },
        {
          id: 1,
          name: "2 Year",
        },
        {
          id: 2,
          name: "3 Year",
        },
        {
          id: 3,
          name: "4 Year",
        },
        {
          id: 4,
          name: "5 Year",
        },
        {
          id: 5,
          name: "6 Year",
        },
        {
          id: 6,
          name: "7 Year",
        },
        {
          id: 7,
          name: "8 Year",
        },
        {
          id: 8,
          name: "9 Year",
        },
        {
          id: 9,
          name: "10 Year",
        },
        {
          id: 10,
          name: "Custom Term",
        },
        {
          id: 11,
          name: "Evergreen Term",
        },
    ];

    const handleRequiredInspectionsSelect = (event) => {
        setRequiredInspections(event.target.value);
    }

    const handleInitialTerms = (event) => {
        setInitialTerms(event.target.value);
    }

    const handleAssignmentProvision = (event) => {
        setAssignmentProvision(event.target.value);
    }

    const handleInstallationCode = (event) => {
        setInstallationCode(event.target.value);
    }

    const handleLegalEntityTypeSelect = (event) => {
        setLegalEntityType(event.target.value);
    }

    return (
        <>
            <Typography variant='h6'>Additional Contract Info</Typography>

            <Grid item xs={12} sx={{paddingTop: 4}}>
                <Box
                    sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: 2,
                    }}
                >

                    <Grid item xs={12} md={6} style={gridStyle}>
                        <DatePickerValue required onChange={setMsaDate} value={msaDate} label={"MSA Date"} />
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <DatePickerValue required onChange={setSowDate} value={sowDate} label={"SOW Date"} />
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <TextField
                            type="number"
                            label="Auto renewal Period (months)"
                            value={autoRenewalPeriod}
                            onChange={(e) => setAutoRenewal(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <FormControl fullWidth>
                            <InputLabel id="am-label">Initial Terms</InputLabel>
                                <Select
                                    labelId="am-label"
                                    id="am-select"
                                    value={initialTerms}
                                    label="Initial Terms"
                                    onChange={handleInitialTerms}
                                >
                                    {initialTermsOptions.map((am) => (
                                    <MenuItem key={am.id} value={am.name}>
                                        {am.name}
                                    </MenuItem>
                                    ))}
                                </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <TextField
                            type="number"
                            label="Cancellation Notice (days)"
                            value={cancellationNotice}
                            onChange={(e) => setCancellationNotice(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <FormControl fullWidth>
                            <InputLabel id="am-label">Required Inspections</InputLabel>
                                <Select
                                    labelId="am-label"
                                    id="am-select"
                                    value={requiredInspections}
                                    label="Required Inspections"
                                    onChange={handleRequiredInspectionsSelect}
                                >
                                    {requiredInspectionsOptions.map((am) => (
                                    <MenuItem key={am.id} value={am.name}>
                                        {am.name}
                                    </MenuItem>
                                    ))}
                                </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <TextField
                            label="Liability Limit Amount"
                            value={liabilityLimitAmount}
                            onChange={(e) => setLiabilityLimitAmount(e.target.value)}
                            name="installValue"
                            id="install-price-input"
                            InputProps={{
                                startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                                ),
                                inputComponent: NumericFormatCustom,
                            }}
                            variant="outlined"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <FormControl fullWidth>
                            <InputLabel id="am-label">Assignment Provision</InputLabel>
                                <Select
                                    labelId="am-label"
                                    id="am-select"
                                    value={assignmentProvision}
                                    label="Assignment Provision"
                                    onChange={handleAssignmentProvision}
                                >
                                    {assignmentProvisionOptions.map((am) => (
                                    <MenuItem key={am.id} value={am.name}>
                                        {am.name}
                                    </MenuItem>
                                    ))}
                                </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <FormControl fullWidth>
                            <InputLabel id="am-label">Installation Code</InputLabel>
                                <Select
                                    labelId="am-label"
                                    id="am-select"
                                    value={installationCode}
                                    label="Installation Code"
                                    onChange={handleInstallationCode}
                                >
                                    {installationCodeOptions.map((am) => (
                                    <MenuItem key={am.id} value={am.name}>
                                        {am.name}
                                    </MenuItem>
                                    ))}
                                </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <TextField
                            label="Company Legal Name"
                            value={companyLegalName}
                            onChange={(e) => setCompanyLegalName(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <TextField
                            label="State of Organization"
                            value={stateOfOrganization}
                            onChange={(e) => setStateOfOrganization(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} style={gridStyle}>
                        <FormControl fullWidth>
                            <InputLabel id="am-label">Legal Entity Type</InputLabel>
                                <Select
                                    labelId="am-label"
                                    id="am-select"
                                    value={legalEntityType}
                                    label="Legal Entity Type"
                                    onChange={handleLegalEntityTypeSelect}
                                >
                                    {legalEntityTypeOptions.map((am) => (
                                    <MenuItem key={am.id} value={am.name}>
                                        {am.name}
                                    </MenuItem>
                                    ))}
                                </Select>
                        </FormControl>
                    </Grid>
                </Box>
            </Grid>
        </>
    );
}